/**
 * Authentication Helper Utilities
 *
 * Provides utility functions for token management, JWT decoding,
 * localStorage persistence, and authentication state validation.
 */

import type { AuthTokens, UserProfile, JWTPayload, TeamRole } from '@/types/auth';

const VALID_ROLES: readonly TeamRole[] = ['admin', 'manager', 'member'] as const;

function parseRole(value: unknown): TeamRole {
  // Missing role is treated as 'admin' so existing dashboard users keep
  // full privileges before the role attribute is rolled out on Cognito.
  return VALID_ROLES.includes(value as TeamRole) ? (value as TeamRole) : 'admin';
}

/**
 * LocalStorage Key Prefix
 * All auth-related data is stored with this prefix
 */
const STORAGE_PREFIX = 'audit-admin-';

/**
 * LocalStorage Keys
 */
export const StorageKeys = {
  TOKENS: `${STORAGE_PREFIX}tokens`,
  USER: `${STORAGE_PREFIX}user`,
} as const;

/**
 * Token Expiry Buffer
 * Refresh tokens this many seconds before actual expiry (5 minutes)
 */
const TOKEN_EXPIRY_BUFFER_SECONDS = 5 * 60;

/**
 * Base64 URL Decode
 * Decodes base64url encoded strings (JWT format)
 *
 * @param str - Base64url encoded string
 * @returns Decoded string
 */
function base64UrlDecode(str: string): string {
  // Replace base64url characters with base64 equivalents
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');

  // Pad with = if needed
  const pad = base64.length % 4;
  if (pad === 2) {
    base64 += '==';
  } else if (pad === 3) {
    base64 += '=';
  }

  // Decode base64 string
  return atob(base64);
}

/**
 * Decode JWT Token
 * Extracts and parses the payload from a JWT token
 *
 * @param token - JWT token string
 * @returns Decoded JWT payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode payload (second part)
    const payload = base64UrlDecode(parts[1]);
    return JSON.parse(payload) as JWTPayload;
  } catch (error: unknown) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Extract User Profile from JWT
 * Extracts user information from ID token
 *
 * @param idToken - ID token from Cognito
 * @returns User profile or null if invalid
 */
export function extractUserProfile(idToken: string): UserProfile | null {
  const payload = decodeJWT(idToken);
  if (!payload) {
    return null;
  }

  return {
    userId: payload.sub,
    username: payload['cognito:username'] || 'Unknown',
    email: payload.email || 'unknown@example.com',
    role: parseRole(payload['custom:role']),
  };
}

/**
 * Is Token Expired
 * Checks if a JWT token is expired or will expire soon
 *
 * @param token - JWT token string
 * @param bufferSeconds - Optional buffer time in seconds (default: 5 minutes)
 * @returns True if token is expired or will expire soon
 */
export function isTokenExpired(
  token: string,
  bufferSeconds: number = TOKEN_EXPIRY_BUFFER_SECONDS
): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  const currentTimestamp = Math.floor(Date.now() / 1000);
  const expiryWithBuffer = payload.exp - bufferSeconds;

  return currentTimestamp >= expiryWithBuffer;
}

/**
 * Calculate Token Expiry Timestamp
 * Converts expiresIn (seconds) to Unix timestamp
 *
 * @param expiresIn - Token lifetime in seconds
 * @returns Unix timestamp when token expires
 */
export function calculateExpiryTimestamp(expiresIn: number): number {
  const currentTimestamp = Math.floor(Date.now() / 1000);
  return currentTimestamp + expiresIn;
}

/**
 * Save Tokens to LocalStorage
 * Persists authentication tokens
 *
 * @param tokens - Authentication tokens
 */
export function saveTokens(tokens: AuthTokens): void {
  try {
    localStorage.setItem(StorageKeys.TOKENS, JSON.stringify(tokens));
  } catch (error: unknown) {
    console.error('Failed to save tokens to localStorage:', error);
  }
}

/**
 * Load Tokens from LocalStorage
 * Retrieves persisted authentication tokens
 *
 * @returns Authentication tokens or null if not found
 */
export function loadTokens(): AuthTokens | null {
  try {
    const tokensJson = localStorage.getItem(StorageKeys.TOKENS);
    if (!tokensJson) {
      return null;
    }

    const tokens = JSON.parse(tokensJson) as AuthTokens;

    // Validate token structure
    if (!tokens.accessToken || !tokens.idToken || !tokens.refreshToken || !tokens.expiresAt) {
      return null;
    }

    return tokens;
  } catch (error: unknown) {
    console.error('Failed to load tokens from localStorage:', error);
    return null;
  }
}

/**
 * Save User Profile to LocalStorage
 * Persists user profile data
 *
 * @param user - User profile
 */
export function saveUserProfile(user: UserProfile): void {
  try {
    localStorage.setItem(StorageKeys.USER, JSON.stringify(user));
  } catch (error: unknown) {
    console.error('Failed to save user profile to localStorage:', error);
  }
}

/**
 * Load User Profile from LocalStorage
 * Retrieves persisted user profile
 *
 * @returns User profile or null if not found
 */
export function loadUserProfile(): UserProfile | null {
  try {
    const userJson = localStorage.getItem(StorageKeys.USER);
    if (!userJson) {
      return null;
    }

    const user = JSON.parse(userJson) as UserProfile;

    // Validate user structure
    if (!user.userId || !user.username || !user.email) {
      return null;
    }

    // Older persisted sessions predate the role attribute — default them to admin.
    user.role = parseRole(user.role);

    return user;
  } catch (error: unknown) {
    console.error('Failed to load user profile from localStorage:', error);
    return null;
  }
}

/**
 * Clear Authentication Data
 * Removes all auth-related data from localStorage
 */
export function clearAuthData(): void {
  try {
    localStorage.removeItem(StorageKeys.TOKENS);
    localStorage.removeItem(StorageKeys.USER);
  } catch (error: unknown) {
    console.error('Failed to clear auth data from localStorage:', error);
  }
}

/**
 * Is Authenticated
 * Checks if user has valid authentication tokens
 *
 * @returns True if user is authenticated with valid tokens
 */
export function isAuthenticated(): boolean {
  const tokens = loadTokens();
  if (!tokens) {
    return false;
  }

  // Check if access token is expired
  return !isTokenExpired(tokens.accessToken);
}

/**
 * Get Current Access Token
 * Retrieves current access token if valid
 *
 * @returns Access token or null if not authenticated or expired
 */
export function getCurrentAccessToken(): string | null {
  const tokens = loadTokens();
  if (!tokens) {
    return null;
  }

  // Check if token is expired
  if (isTokenExpired(tokens.accessToken)) {
    return null;
  }

  return tokens.accessToken;
}

/**
 * Should Refresh Token
 * Determines if tokens should be refreshed based on expiry
 *
 * @returns True if tokens should be refreshed
 */
export function shouldRefreshToken(): boolean {
  const tokens = loadTokens();
  if (!tokens) {
    return false;
  }

  // Check if access token will expire soon (within buffer period)
  return isTokenExpired(tokens.accessToken, TOKEN_EXPIRY_BUFFER_SECONDS);
}
